import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import queryString from 'querystring';

import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import LayoutPicker from '@prisjakt/ui-layout-picker';
import Dropdown from '@prisjakt/ui-dropdown';
import Breadcrumb from '@prisjakt/ui-breadcrumb';

import FilterIcon from 'mdi-react/FilterVariantIcon';

import Experiment from '@prisjakt/ui-experiment';
import MatchMedia from '../../components/match-media';
import PageHeader from '../../components/page-header';
import CampaignDeals from '../../components/campaign-deals';
import SubCategoryListing from './components/sub-category-listing';
import VisibilityToggle from './components/sub-category-listing/components/toggle';
import ProductListing from './components/product-listing';
import { HorizontalFilter } from './components/filter';
import FilterMobileToggle from './components/filter/components/mobile-toggle';
import SelectedFilters from './components/filter/components/selected-filters';
import PageContent from '../../components/page-content';
import CategoryInformation from './components/category-information';
import FeaturedArticle from '../../components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import NewProductReleases from '../../components/new-product-releases';
import AdContainer from '../../components/ads';
import Counter from './components/filter/components/counter';
import { CategoryFragment } from '../../types';
import {
  trackComparisonSelectionStarted,
  trackComparisonSelectionAborted,
  trackComparisonSelectionChanged,
} from '../product-compare/tracking';

import { CategoryList } from '../../components/category-list';
import CompareProductBasket from './components/compare-product-basket';
import CompareProductToggle from './components/compare-product-toggle';

import sidebarFilterActions from '../sidebar/actions/filter';
import { setHorizontalFilter } from './actions/filter';
import { categoryLayoutRequest } from './actions/category';
import { sortChange, adsViewUpdate } from '../../globals/actions';

import { SIDEBAR_STATE } from '../sidebar/constants';

import { LOCAL_STORAGE_FILTER_KEY } from './constants';

import * as S from './styles';
import './index.scss';
import './sagas/index';

const styles = bemGenerator('category-page', [
  'wrapper',
  'gutter',
  'product-wrapper',
  'header',
  'header-info',
  'sub-header',
  'header-article-link',
  'categories',
  'product-count',
  'products',
  'products-header-buttons',
  'articles',
  'article',
  'dailyDeals',
  'buttons',
  'filter',
  'media',
  'description',
  'description-text',
  'aliases',
  'selected-filters',
  'classic-notice',
  'expand-description-button',
  'adWrapper',
]);

const isVirtualCategory = categoryId => /^v/.test(categoryId);
const sortOptions = ['name', 'popularity', 'user_reviews'];
const bothDirectionsSortOptions = ['price', 'price_include_shipping'];
const compareProductLimit = 40;

class Category extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      descriptionCollapsed: true,
      subCategoriesCollapsed: true,
      showProductSelector: false,
      selectedProducts: [],
    };

    this.onSortChange = this.onSortChange.bind(this);
    this.onProductSelect = this.onProductSelect.bind(this);
    this.toggleFilter = this.toggleFilter.bind(this);
    this.onProductDeselect = this.onProductDeselect.bind(this);
    this.onActivateProductSelections = this.onActivateProductSelections.bind(
      this
    );
    this.onClearProductSelectionsCompareButton = this.onClearProductSelections.bind(
      this,
      'compareButton'
    );
    this.onClearProductSelectionsBasket = this.onClearProductSelections.bind(
      this,
      'basket'
    );
  }

  componentDidMount() {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    this.props.adsViewUpdate('category:mounted');

    try {
      const showHorizontalFilter = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_FILTER_KEY)
      );
      this.props.setHorizontalFilter(showHorizontalFilter);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  getDropdownSortOptions(sortData) {
    const { t } = this.context;

    const bothDirectionsOptions = bothDirectionsSortOptions
      .filter(x => sortData.available.includes(x))
      .reduce(
        (arr, option) => [
          {
            name: t('common', `sort.${option}:asc`),
            value: `${option}:asc`,
          },
          {
            name: t('common', `sort.${option}:desc`),
            value: `${option}:desc`,
          },
          ...arr,
        ],
        []
      )
      .filter(x => x.value !== `${sortData.name}:${sortData.direction}`);

    return sortOptions
      .filter(x => sortData.available.includes(x))
      .filter(
        x => x !== sortData.name && !bothDirectionsSortOptions.includes(x)
      )
      .map(option => ({
        name: t('common', `sort.${option}`),
        value: `${option}`,
      }))
      .concat(bothDirectionsOptions);
  }

  getDropdownSortSelected(sortData) {
    const { t } = this.context;

    return sortData.name.indexOf('property:') !== 0
      ? t(
          'common',
          `sort.${sortData.name}${
            bothDirectionsSortOptions.includes(sortData.name)
              ? `:${sortData.direction}`
              : ''
          }`
        )
      : '';
  }

  renderHeader(mode, pageInfo, matchMedia) {
    const { isMobile } = matchMedia;
    const { t } = this.context;
    const {
      name,
      productCount,
      storesCount,
      immediateProducts,
    } = this.props.data.category;

    const { showProductSelector } = this.state;

    const productCountText = t('category', 'products.count', {
      n: productCount,
    });
    const headerTitle = isMobile ? '' : t('category', 'products.title');
    const headerSubtitle = isMobile ? '' : productCountText;
    const { error: filterError, query } = this.props.categorySettings.filter;
    const count = Object.keys(query).length;
    const buttonColor = 'primary';
    const filterIsOpen =
      this.props.sidebarSettings &&
      this.props.sidebarSettings.open &&
      this.props.sidebarSettings.tab === SIDEBAR_STATE.FILTER;

    return (
      <Fragment>
        {immediateProducts ? (
          <S.PanelHeaderWrapper>
            <S.PanelHeader
              title={headerTitle}
              subtitle={headerSubtitle}
              divider={isMobile ? 'both' : 'none'}
            >
              {!filterError && !this.props.showHorizontalFilter && (
                <S.FilterButton
                  color={buttonColor}
                  variant={filterIsOpen ? 'default' : 'outlined'}
                  onClick={() => this.props.toggleFilter()}
                  icon={
                    count ? (
                      <Counter
                        count={count}
                        variant={filterIsOpen ? 'light' : 'dark'}
                        color={buttonColor}
                      />
                    ) : (
                      <FilterIcon size="18" />
                    )
                  }
                >
                  {t('filter', 'filter.title')}
                </S.FilterButton>
              )}
              <Dropdown
                title={isMobile ? '' : t('common', 'sort.title')}
                options={this.getDropdownSortOptions(pageInfo.sort)}
                selected={this.getDropdownSortSelected(pageInfo.sort)}
                onSelect={option => {
                  this.onSortChange(option.value);
                }}
                data-test="SortOrderDropdown"
              />
              <div className={styles['products-header-buttons']}>
                <LayoutPicker
                  mode={mode}
                  onClick={this.props.categoryLayoutRequest}
                />
                <CompareProductToggle
                  active={showProductSelector}
                  activate={this.onActivateProductSelections}
                  deactivate={this.onClearProductSelectionsCompareButton}
                />
              </div>
            </S.PanelHeader>
          </S.PanelHeaderWrapper>
        ) : (
          <PanelHeader
            title={t('category', 'popular-products.title', {
              categoryName: name,
            })}
            divider="none"
          />
        )}

        <CategoryInformation
          statistics={{
            productsCount: productCount,
            storesCount,
          }}
        />

        {immediateProducts ? (
          <div className={styles['product-count']}>{productCountText}</div>
        ) : null}
      </Fragment>
    );
  }

  getDescriptionData() {
    const { products, immediateProducts } = this.props.data.category;
    const categories = this.props.data.category.categories;

    if (categories && categories.nodes.length !== 0) {
      return categories.nodes
        .slice(0, 20)
        .map(c => c.name)
        .join(', ');
    }

    return Category.products({ products, immediateProducts })
      .nodes.slice(0, 20)
      .map(p => p.name)
      .join(', ');
  }

  onSortChange(value) {
    const location = this.props.history.location;
    const qs = queryString.decode(location.search.substr(1));

    const selectedSortProperty = value.replace(/:(asc|desc)$/, '');
    const selectedSortDirection = value.match(/:(asc|desc)$/);

    const currentSortDirection = this.props.data.category.products.pageInfo.sort
      .direction;

    const data = { replaceHistory: true };

    if (selectedSortDirection) {
      // Sort direction was selected from dropdown
      data.value = `${selectedSortProperty}:${selectedSortDirection[1]}`;
    } else if (qs.sort === selectedSortProperty) {
      // Same sort property, then we toggle the current sort direction
      data.value = `${selectedSortProperty}:${
        currentSortDirection === 'asc' ? 'desc' : 'asc'
      }`;
    } else {
      // Changed to a new sort property
      data.value = `${selectedSortProperty}`;
    }

    delete qs.offset;

    this.props.sortChange(data);
  }

  onProductSelect(productId) {
    return () => {
      if (this.state.selectedProducts.length > compareProductLimit) {
        return;
      }

      const { products } = this.props.data.category;

      const selectedProduct = products.nodes.find(
        product => product.id === productId
      );

      const alreadySelected = this.state.selectedProducts.find(
        product => product.id === productId
      );

      if (selectedProduct && !alreadySelected) {
        this.setState(
          state => ({
            selectedProducts: [...state.selectedProducts, selectedProduct],
          }),
          () => {
            trackComparisonSelectionChanged(
              this.state.selectedProducts.length,
              'add',
              'productCard'
            );
          }
        );
      }
    };
  }

  onProductDeselect(productId, source = 'productCard') {
    return () => {
      const selectedProduct = this.state.selectedProducts.find(
        product => product.id === productId
      );

      if (selectedProduct) {
        this.setState(
          state => ({
            selectedProducts: state.selectedProducts.filter(
              product => product.id !== selectedProduct.id
            ),
          }),
          () => {
            trackComparisonSelectionChanged(
              this.state.selectedProducts.length,
              'remove',
              source
            );
          }
        );
      }
    };
  }

  onActivateProductSelections() {
    this.setState({
      showProductSelector: true,
    });
    trackComparisonSelectionStarted();
  }

  onClearProductSelections(source) {
    this.setState({
      showProductSelector: false,
      selectedProducts: [],
    });
    trackComparisonSelectionAborted(source);
  }

  renderProductListing(matchMedia) {
    const { data } = this.props;
    const countryCode = this.context.market.code;
    const id = data.category.id;
    const { isDesktop } = matchMedia;
    const { categorySettings, propertySummaryMode } = this.props;
    const {
      immediateProducts,
      products,
      logo,
      defaultLayoutMode,
    } = this.props.data.category;
    const { comparing, error: filterError } = categorySettings.filter;
    const showFilter = immediateProducts && !filterError;
    const selectedProperties = products.pageInfo.selectedProperties.filter(x =>
      comparing.includes(x.id)
    );

    const productListing = (layoutMode, tvsWithSize = false) => {
      const mode =
        (!immediateProducts && 'grid') ||
        (categorySettings.category && categorySettings.category.mode) ||
        layoutMode ||
        'grid';
      return (
        <ProductListing
          tvsWithSize={tvsWithSize}
          header={this.renderHeader(mode, products.pageInfo, matchMedia)}
          products={Category.products({
            products,
            immediateProducts,
          })}
          selectedProperties={selectedProperties}
          mode={this.getListRenderMode(mode, isDesktop)}
          pageInfo={products.pageInfo}
          withFilter={showFilter}
          activeCampaign={this.props.activeCampaign}
          propertySummaryMode={propertySummaryMode}
          onSortChange={this.onSortChange}
          fallbackImage={logo}
          showPagination={immediateProducts}
          showRatings={immediateProducts}
          showCategory={!immediateProducts}
          showProductSelector={
            immediateProducts && this.state.showProductSelector
          }
          selectedProducts={this.state.selectedProducts}
          onProductSelect={this.onProductSelect}
          onProductDeselect={this.onProductDeselect}
          adverts={{
            desktopInline: (
              <AdContainer
                scalable
                scaleBase=".pj-ui-panel"
                scaleOrigin="left center"
                placement={
                  immediateProducts ? 'panorama_extra' : 'panorama_target'
                }
                min={861}
                max={10000}
                spacing={{ bottom: '10px' }}
              />
            ),
          }}
        />
      );
    };
    if (
      countryCode === 'gb' &&
      (id === '350' ||
        id === '585' ||
        id === '893' ||
        id === '358' ||
        id === '360' ||
        id === '409' ||
        id === '336' ||
        id === '586' ||
        id === '1702' ||
        id === '1790' ||
        id === '377' ||
        id === '980' ||
        id === '483' ||
        id === '1045' ||
        id === '500' ||
        id === '1320' ||
        id === '373')
    ) {
      return (
        <Experiment
          id="category-view-in-computer-components"
          baseline={{ categoryMode: 'row' }}
          variants={{
            grid: { categoryMode: 'grid' },
          }}
          render={variant => productListing(variant.categoryMode)}
        />
      );
    }
    if (id === '107' && countryCode === 'gb') {
      const tvLayoutMode =
        (!immediateProducts && 'grid') ||
        (categorySettings.category && categorySettings.category.mode) ||
        defaultLayoutMode ||
        'grid';
      if (tvLayoutMode === 'grid') {
        return (
          <Experiment
            id="tvs-with-size-in-names"
            baseline={{ withSize: false }}
            variants={{ tvsWithSize: { withSize: true } }}
            render={variant =>
              productListing(defaultLayoutMode, variant.withSize)
            }
          />
        );
      }
      return productListing(defaultLayoutMode);
    }
    return productListing(defaultLayoutMode);
  }

  renderArticles() {
    const { category } = this.props.data;
    const { articles, name } = category;
    const featuredArticle = articles
      ? articles.find(item => item.featured)
      : null;
    if (!articles || articles.length === 0) {
      return null;
    }
    return (
      <div className={`${styles.gutter} ${styles.articles}`}>
        <Panel
          header={
            <PanelHeader
              title={this.context.t('category', 'article.related-articles', {
                categoryName: name,
              })}
            />
          }
        >
          <FeaturedArticle article={featuredArticle} related={articles} />
        </Panel>
      </div>
    );
  }

  getListRenderMode(mode, isDesktop) {
    const { immediateProducts } = this.props.data.category;

    // Render row mode as a table if category has filter and everything
    if (mode === 'row' && immediateProducts && isDesktop) {
      return 'table';
    }

    return mode;
  }

  static products({ products, immediateProducts }) {
    if (!products || immediateProducts) {
      return products;
    }

    // For non-leaf categories, we want to re-sort the fetched products to showcase more sub-categories,
    // and only render the first 12
    return {
      ...products,
      nodes: products.nodes
        ? products.nodes
            .sort((a, b) => {
              if (a.popularity.inCategory === b.popularity.inCategory) {
                return a.popularity.total > b.popularity.total ? 1 : -1;
              }

              return a.popularity.inCategory > b.popularity.inCategory ? 1 : -1;
            })
            .slice(0, 12)
        : products.nodes,
    };
  }

  toggleFilter() {
    this.props.setHorizontalFilter(!this.props.showHorizontalFilter);
  }

  render() {
    const { t, linkTo } = this.context;

    const { categorySettings } = this.props;
    const {
      id,
      immediateProducts,
      products,
      categories,
      filterId,
      brand,
      brands,
      name,
      aliases,
      description,
      logo,
      path,
      pathName,
      defaultLayoutMode,
      productCount,
      deals,
      newReleases,
      preReleases,
      categoryTree,
    } = this.props.data.category;

    const {
      descriptionCollapsed,
      subCategoriesCollapsed,
      showProductSelector,
      selectedProducts,
    } = this.state;

    const mode =
      (!immediateProducts && 'grid') ||
      (categorySettings.category && categorySettings.category.mode) ||
      defaultLayoutMode;

    const { mobileOpen, query, error: filterError } = categorySettings.filter;

    const hasSubCategories = categories && categories.nodes.length > 0;
    const hasSubBrandCategories =
      !isVirtualCategory(id) && brands && brands.nodes.length > 0;
    const brandCategories = hasSubBrandCategories
      ? brands.nodes.map(categoryBrand => ({
          id: `${id}:${categoryBrand.id}`,
          name: `${categoryBrand.name} ${name}`,
          pathName: linkTo('brandCategory', {
            id,
            pathName,
            brandId: categoryBrand.id,
          }),
        }))
      : [];

    const campaignPromoBanners = {
      before:
        this.props.campaignBanners &&
        this.props.campaignBanners.active.filter(
          b => b && b.type === 'BEFORE_PROMO_BANNER'
        ),
      during:
        this.props.campaignBanners &&
        this.props.campaignBanners.active.filter(
          b => b && b.type === 'DURING_PROMO_BANNER'
        ),
    };

    const showFilter = immediateProducts && !filterError;

    return (
      <MatchMedia
        debounce={150}
        serverSideRender={{ isDesktop: true }}
        matches={[
          {
            mediaQuery: '(max-width: 640px)',
            values: { isMobile: true },
          },
          {
            mediaQuery: '(min-width: 640px) and (max-width: 1024px)',
            values: { isTablet: true },
          },
          {
            mediaQuery: '(min-width: 1024px)',
            values: { isDesktop: true },
          },
        ]}
      >
        {matchMedia => {
          const { isMobile, isDesktop } = matchMedia;
          return (
            <div
              className={styles.block.withModifier({
                'mobile-open': mobileOpen,
              })}
            >
              <PageContent withAds data-test="CategoryPage">
                {campaignPromoBanners.during &&
                  campaignPromoBanners.during.length > 0 && (
                    <div className={styles.gutter}>
                      <PromoBanner banners={campaignPromoBanners.during} />
                    </div>
                  )}
                <Breadcrumb
                  homeText={t('common', 'breadcrumb.home')}
                  path={path}
                />

                <div className={styles.wrapper}>
                  <div className={styles.header}>
                    <PageHeader
                      className={styles['header-info']}
                      media={
                        logo && (
                          <img
                            className={styles.media}
                            width={100}
                            alt={name}
                            src={logo}
                          />
                        )
                      }
                      title={name}
                      right={
                        brand && brand.featured && brand.logo ? (
                          <S.BrandLink to={linkTo('brand', brand)}>
                            <S.BrandLogo src={brand.logo} alt={brand.name} />
                          </S.BrandLink>
                        ) : null
                      }
                    >
                      {/* Don't show the category description on brand category pages */}
                      {!brand && (
                        <div
                          className={styles.description.withModifier({
                            collapsed: descriptionCollapsed && isMobile,
                          })}
                          data-test="CategoryDescription"
                        >
                          <div className={styles['description-text']}>
                            <span
                              dangerouslySetInnerHTML={{ __html: description }}
                            />
                            {aliases && aliases.length > 0 && (
                              <div className={styles.aliases}>
                                {t('category', 'category.alias', {
                                  alias: aliases.join(', '),
                                })}
                              </div>
                            )}
                          </div>
                          {descriptionCollapsed && isMobile && (
                            <button
                              type="button"
                              className={styles['expand-description-button']}
                              onClick={() => {
                                this.setState({
                                  descriptionCollapsed: false,
                                });
                              }}
                              aria-label={t('symbols', 'symbol.expand')}
                            />
                          )}
                        </div>
                      )}
                    </PageHeader>
                  </div>

                  {(hasSubCategories || hasSubBrandCategories) &&
                    isDesktop &&
                    immediateProducts && (
                      <div className={styles.categories}>
                        <div className={styles.gutter}>
                          <SubCategoryListing
                            styling="inline-list"
                            categories={categories}
                            brands={brandCategories}
                          />
                        </div>
                      </div>
                    )}

                  {hasSubCategories && !immediateProducts && categoryTree && (
                    <div className={styles.categories}>
                      <div className={styles.gutter}>
                        <>
                          <CategoryList categories={categoryTree} withAds />

                          <div className={styles.adWrapper}>
                            <AdContainer
                              scalable
                              scaleBase=".category-page--adWrapper"
                              placement="panorama_extra"
                              min={861}
                              max={10000}
                              spacing={{ top: '0', bottom: '10px' }}
                            />
                          </div>
                        </>
                      </div>
                    </div>
                  )}

                  {showFilter && !mobileOpen && (
                    <div className={styles['sub-header']}>
                      <S.Column fullWidth={!hasSubCategories}>
                        <FilterMobileToggle filterSettings={query} />
                      </S.Column>
                      {!isDesktop && hasSubCategories && (
                        <React.Fragment>
                          <S.Column>
                            <VisibilityToggle
                              active={!subCategoriesCollapsed}
                              onToggle={() =>
                                this.setState({
                                  subCategoriesCollapsed: !subCategoriesCollapsed,
                                })
                              }
                              label={t('category', 'subcategories.show')}
                            />
                          </S.Column>
                          <S.Column fullWidth>
                            <SubCategoryListing
                              styling="list"
                              categories={categories}
                              collapse={subCategoriesCollapsed}
                            />
                          </S.Column>
                        </React.Fragment>
                      )}
                      <S.Column fullWidth>
                        <SelectedFilters
                          categoryId={id}
                          className={styles['selected-filters']}
                        />
                      </S.Column>
                    </div>
                  )}

                  <>
                    {showFilter &&
                      isDesktop &&
                      this.props.showHorizontalFilter && (
                        <HorizontalFilter
                          categoryId={id}
                          filterId={filterId}
                          brand={brand}
                          productCount={productCount}
                          canCompare={mode === 'row'}
                          quickFilters={[]}
                          className="column"
                          toggleFilter={this.toggleFilter}
                          expanded={this.props.showHorizontalFilter}
                        />
                      )}

                    {(products.nodes.length > 0 || immediateProducts) && (
                      <div className={styles.gutter}>
                        <div className={styles['product-wrapper']}>
                          {showFilter && !isDesktop && (
                            <S.Filter
                              open={mobileOpen}
                              categoryId={id}
                              filterId={filterId}
                              brand={brand}
                              productCount={productCount}
                              canCompare={mode === 'row'}
                              quickFilters={[]}
                              showFilterToggle={isDesktop}
                              toggleFilter={this.toggleFilter}
                              expanded={this.props.showHorizontalFilter}
                            />
                          )}
                          <div
                            className={styles.products}
                            data-test="ProductListing"
                            data-test-category={this.props.data.category.id}
                          >
                            {this.renderProductListing(matchMedia)}
                            {showProductSelector && (
                              <CompareProductBasket
                                products={selectedProducts}
                                compareUrl={
                                  selectedProducts.length > 1
                                    ? linkTo('productCompare', {
                                        category: this.props.data.category,
                                        products: selectedProducts,
                                      })
                                    : undefined
                                }
                                onEmpty={this.onClearProductSelectionsBasket}
                                onProductDelete={this.onProductDeselect}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>

                  {campaignPromoBanners.before &&
                    campaignPromoBanners.before.length > 0 && (
                      <div className={styles.gutter}>
                        <PromoBanner
                          inverted
                          banners={campaignPromoBanners.before}
                        />
                      </div>
                    )}

                  {this.renderArticles()}
                  {deals && deals.length > 0 && (
                    <div className={`${styles.gutter} ${styles.dailyDeals}`}>
                      <CampaignDeals
                        deals={deals}
                        campaignLinkQuery={`?category=${id}`}
                      />
                    </div>
                  )}

                  <div className={styles.gutter}>
                    <NewProductReleases
                      newReleases={
                        newReleases && newReleases.nodes.length > 3
                          ? newReleases
                          : undefined
                      }
                      preReleases={
                        preReleases && preReleases.nodes.length > 3
                          ? preReleases
                          : undefined
                      }
                      personalized={false}
                    />

                    <RecentlyVisitedProducts />
                  </div>
                </div>
              </PageContent>
            </div>
          );
        }}
      </MatchMedia>
    );
  }
}

const { arrayOf, bool, func, number, object, oneOf, shape, string } = PropTypes;

Category.propTypes = {
  data: shape({
    category: graphqlPropTypes(CategoryFragment),
  }).isRequired,
  categorySettings: shape({
    category: shape({
      mode: string,
    }),
    filter: shape({
      mobileOpen: bool.isRequired,
    }),
  }).isRequired,
  categoryLayoutRequest: func.isRequired,
  categorySortRequest: func,
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        type: string,
        url: string,
        imageUrl: string,
      })
    ),
  }),
  propertySummaryMode: string,
  brand: number,
  defaultLayoutMode: oneOf(['grid', 'row']),
  campaignBanners: shape({
    active: arrayOf(object),
  }),
  adsViewUpdate: func.isRequired,
  sortChange: func.isRequired,
  toggleFilter: func.isRequired,
  sidebarSettings: shape({
    open: bool,
    tab: string,
  }),
  showHorizontalFilter: bool,
  setHorizontalFilter: func,
};

Category.defaultProps = {
  categorySortRequest: () => {},
  activeCampaign: undefined,
  propertySummaryMode: 'verbose',
  brand: null,
  defaultLayoutMode: 'grid',
  campaignBanners: undefined,
  sidebarSettings: {},
  showHorizontalFilter: false,
  setHorizontalFilter: () => {},
};

const mapStateToProps = state => ({
  categorySettings: state.areas.category,
  campaignBanners: state.globals.campaignBanners,
  sidebarSettings: state.areas.sidebar,
  showHorizontalFilter: state.areas.category.filter.showHorizontalFilter,
});

const dispatchProps = {
  toggleFilter: sidebarFilterActions.toggleFilter,
  sortChange,
  adsViewUpdate,
  categoryLayoutRequest,
  setHorizontalFilter,
};

export default connect(
  mapStateToProps,
  dispatchProps
)(withRouter(Category));

Category.contextType = AppContext;
